import { isEqual, isWithinInterval } from "date-fns";
import { concat } from "lodash";
import {
  StaffMembersWorkShift,
  WorkShiftEvent,
} from "services/types/events.type";
import { StaffMember } from "services/types/staff.type";

// Helper function to create unique staff member schedule identifier
const createStaffMemberScheduleId = (
  staffMemberId: string | number,
  clinicId: string | number
) => `${staffMemberId};${clinicId}`;

// Helper function to convert a schedule interval into a timestamp range string
const createTimeSlotKey = (start: Date, end: Date) =>
  `${start.getTime()};${end.getTime()}`;

// Converts string intervals to Date arrays for easier manipulation
const convertStringToDateArray = (scheduleIntervals: string[]): Date[][] =>
  scheduleIntervals.map((interval) =>
    interval.split(";").map((timestamp) => new Date(+timestamp))
  );

// Merges consecutive time slots for the same clinic and staff member
const mergeTimeSlots = (scheduleIntervals: string[]): string[][] => {
  const sortedIntervals = convertStringToDateArray(scheduleIntervals).sort(
    ([a], [b]) => a.getTime() - b.getTime()
  );

  const mergedDateIntervals = sortedIntervals.reduce<Date[][]>(
    (merged, current) => {
      if (!merged.length || current[0] > merged[merged.length - 1][1]) {
        merged.push(current);
      } else if (current[1] > merged[merged.length - 1][1]) {
        merged[merged.length - 1][1] = current[1];
      }
      return merged;
    },
    []
  );

  // Convert back to ISO strings for serializable storage
  return mergedDateIntervals.map(([start, end]) => [
    start.toISOString(),
    end.toISOString(),
  ]);
};

// Main function to build staff member work shifts
export const buildStaffMemberWorkShift = (
  events: WorkShiftEvent[]
): StaffMembersWorkShift => {
  //StaffMember schedule record, where key = "{staffMemberId};{clinicId}", value = set of values "start_moment;end_moment"
  const staffMemberSchedule: Record<string, Set<string>> = {};

  events.forEach((event) => {
    const { start_moment, end_moment, staff_member } = event;
    const staffMemberId = (staff_member as Partial<StaffMember>)?.id;
    const clinic = event.clinic.id;
    if (!staffMemberId) return;
    if (event.extra.is_available) {
      const scheduleId = createStaffMemberScheduleId(staffMemberId, clinic);
      const timeSlotKey = createTimeSlotKey(
        new Date(start_moment),
        new Date(end_moment)
      );

      const timeSlots = staffMemberSchedule[scheduleId] || new Set<string>();
      timeSlots.add(timeSlotKey);
      staffMemberSchedule[scheduleId] = timeSlots;
    }
  });

  // Convert and merge time slots for each staff member and clinic
  const mergedTimeSlots: StaffMembersWorkShift = {};
  Object.keys(staffMemberSchedule).forEach((key) => {
    const timeSlots = staffMemberSchedule[key];
    const mergedSlots = mergeTimeSlots(Array.from(timeSlots));
    mergedTimeSlots[key] = mergedSlots;
  });

  return mergedTimeSlots;
};

export const isStaffMemberAvailableUtil = ({
  staffMemberId,
  clinicId,
  startDate,
  endDate,
  workShift,
}: {
  startDate: Date;
  endDate?: Date;
  staffMemberId?: number;
  clinicId?: number;
  workShift?: StaffMembersWorkShift;
}) => {
  if (!staffMemberId || !startDate || !workShift) return false;

  let intervals: string[][] | undefined = [];

  if (clinicId) {
    const staffMemberScheduleId = createStaffMemberScheduleId(
      staffMemberId,
      clinicId
    );

    intervals = workShift[staffMemberScheduleId];
  } else {
    Object.keys(workShift)
      .filter((key) => key.startsWith(`${staffMemberId};`))
      .forEach((key) => {
        intervals = concat(intervals ?? [], workShift[key] ?? []);
      });
  }

  if (!intervals?.length) return false;

  return intervals.some(([startIso, endIso]) => {
    const start = new Date(startIso);
    const end = new Date(endIso);

    startDate.setSeconds(0);
    start.setSeconds(0);
    end.setSeconds(0);
    if (!endDate) {
      return (
        isWithinInterval(startDate, { start, end }) && !isEqual(startDate, end)
      );
    }
    return (
      isWithinInterval(startDate, { start, end }) &&
      !isEqual(startDate, end) &&
      (isWithinInterval(endDate, { start, end }) || isEqual(endDate, end))
    );
  });
};
